<template>
  <div v-loading="loading">
    <el-upload
      ref="resourcesUp"
      :accept="accept"
      :auto-upload="autoUpload"
      :before-remove="beforeRemove"
      :before-upload="beforeUpload"
      :file-list="fileList"
      :http-request="enclosureUpload"
      :limit="limit"
      :multiple="multiple"
      :on-change="onFileChange"
      :on-exceed="handleExceed"
      :on-remove="handleRemove"
      :show-file-list="showFileList"
      action=""
      class="drag-upload-cmp"
      drag
    >
      <i class="el-icon-upload" />
      <div class="el-upload__text">{{ uploadText }}</div>
      <div v-if="tips" slot="tip" class="el-upload__tip">{{ tips }}</div>
    </el-upload>
  </div>
</template>

<script>
import { uploadOos } from '@/utils/ali_oos_util';
import i18n from '@/lang/index.js';

export default {
  name: 'DragUploadCmp',

  props: {
    // 回显文件列表
    fileArr: {
      type: Array,
      default: () => []
    },

    // 文件在服务器上的文件夹名称
    serverFolderName: {
      type: String,
      default: 'finance'
    },

    // 限制上传文件大小 单位MB
    fileSize: {
      type: Number,
      default: 5
    },

    /**
     * 文件类型（MIME），用于 before-upload 校验
     * 如 excel：['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
     */
    fileType: {
      type: Array,
      default: () => []
    },

    // 限制上传文件数量
    limit: {
      type: Number,
      default: 1
    },

    // 是否多选
    multiple: {
      type: Boolean,
      default: false
    },

    // 提示文字（显示在拖拽区下方）
    tips: {
      type: String,
      default: ''
    },

    // 上传框内文字提示
    uploadText: {
      type: String,
      default: i18n.t('basicData.DragTheFileHereOrClickUpload')
    },

    // 是否显示文件列表
    showFileList: {
      type: Boolean,
      default: true
    },

    // 是否自动上传到 OSS；false 时仅选取文件并把原始 File 通过 change 事件抛出
    autoUpload: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      fileList: [],
      loading: false
    };
  },

  computed: {
    accept() {
      return this.fileType.length > 0 ? this.fileType.join(',') : '';
    }
  },

  watch: {
    fileArr: {
      handler(newVal) {
        this.fileList = newVal;
      },
      immediate: true,
      deep: true
    }
  },

  methods: {
    /**
     * el-upload on-change 钩子
     * 当 autoUpload=false 时，文件选取后立即抛出原始 File，不进行 OSS 上传
     */
    onFileChange(file, fileList) {
      if (this.autoUpload) return; // 自动上传模式交给 http-request

      // beforeUpload 失败时不处理
      if (file.status === 'ready') {
        if (!this.beforeUpload(file.raw)) {
          // 校验失败：从 el-upload 内部列表移除
          const idx = fileList.findIndex((item) => item.uid === file.uid);
          if (idx > -1) fileList.splice(idx, 1);
          this.fileList = fileList;
          return;
        }
      }

      // 只保留校验通过的文件，附带原始 File 对象
      this.fileList = fileList.map((item) => ({
        name: item.name,
        uid: item.uid,
        raw: item.raw,
        status: item.status
      }));

      this.$emit('change', this.fileList);
    },

    // 文件上传（仅 autoUpload=true 时由 el-upload 触发）
    enclosureUpload(option) {
      const file = option.file;

      if (this.limit === 1) {
        this.fileList = [];
      }

      this.loading = true;
      uploadOos(file, this.serverFolderName)
        .then((res) => {
          if (res.code === 200) {
            const obj = {
              name: file.name,
              url: res.url,
              uid: file.uid
            };

            this.fileList.push(obj);

            this.$message.success(`${file.name} ${this.$t('basicData.uploadSuccessful')}`);

            const fileData = this.showFileList ? this.fileList : [obj];
            this.$emit('change', fileData);
          } else {
            this.handleUploadFailure(file, res.msg);
          }
        })
        .catch(() => {
          this.handleUploadFailure(file);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // 处理上传失败
    handleUploadFailure(file, msg) {
      this.$message.error(`${file.name} ${msg || this.$t('basicData.uploadFailed')}`);
      this.removeFailedFile(file);
      this.loading = false;
    },

    // 上传前校验
    beforeUpload(file) {
      const isOverSize = file.size / 1024 / 1024 > this.fileSize;
      if (isOverSize) {
        this.$message.warning(this.$t('basicData.fileSizeExceedsLimit', { size: this.fileSize }));
        return false;
      }

      if (this.fileType.length > 0) {
        const isMatch = this.fileType.includes(file.type);
        if (!isMatch) {
          this.$message.warning(this.$t('basicData.invalidFileFormat'));
          return false;
        }
      }

      return true;
    },

    // 数量超限
    handleExceed() {
      this.$message.warning(this.$t('basicData.fileSelectionLimit', { size: this.limit }));
    },

    // 删除前提示
    beforeRemove(file) {
      return this.$confirm(`确定移除 ${file.name}？`, this.$t('basicData.tips'));
    },

    // 确认删除
    handleRemove(file, fileList) {
      this.fileList = fileList;
      this.$emit('change', this.fileList);
    },

    // 移除失败文件
    removeFailedFile(file) {
      if (!this.$refs.resourcesUp || !file || !file.uid) return;

      const uploadFiles = this.$refs.resourcesUp.uploadFiles;
      if (!uploadFiles || !Array.isArray(uploadFiles)) return;

      const index = uploadFiles.findIndex((item) => item.uid === file.uid);
      if (index > -1) {
        uploadFiles.splice(index, 1);
      }
    },

    // 清空文件列表
    clearFiles() {
      this.fileList = [];
      if (this.$refs.resourcesUp) {
        this.$refs.resourcesUp.clearFiles();
      }
    }
  }
};
</script>

<style scoped>
.drag-upload-cmp ::v-deep .el-upload {
  display: block;
}

.drag-upload-cmp ::v-deep .el-upload-dragger {
  width: 100%;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>
